<template>
    <div>
        <ticto-loading v-if="loading" />
        
        <div class="my-5">
            <div class="flex items-center justify-between">
                <div class="flex items-center">
                    <ClockIcon
                        class="mr-2 h-8 w-7 shrink-0 text-gray-400"
                    />
                    <h3 class="mb-5 text-lg font-medium leading-9 dark:text-white">
                        Timeline - Subscrição de Tópicos
                    </h3>
                </div>
                <div class="mb-5">
                    <router-link
                        to="/mobile"
                        class="inline-flex items-center justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-gray-500"
                    >
                        <DevicePhoneMobileIcon
                            class="mr-2 h-5 w-5"
                        />
                        Voltar para Mobile
                    </router-link>
                </div>
            </div>

            <filters
                v-model="filters"
                @fetch="
                    pagination.current_page = 1;
                    executions = [];
                    fetch();
                "
                :loading="loading"
                class="my-5"
            >
                <template #filters>
                    <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div>
                            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                            <vue-select
                                v-model="filters.status"
                                :options="statusOptions"
                                placeholder="Todos os status"
                                :clearable="true"
                            />
                        </div>
                        <div>
                            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Data Início</label>
                            <input
                                type="datetime-local"
                                v-model="filters.date_from"
                                class="text-input"
                            />
                        </div>
                        <div>
                            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Data Fim</label>
                            <input
                                type="datetime-local"
                                v-model="filters.date_to"
                                class="text-input"
                            />
                        </div>
                    </div>
                </template>
            </filters>

            <div class="mt-10">
                <h3 class="mb-5 text-lg font-medium leading-6 dark:text-white">
                    Execuções
                </h3>

                <div class="space-y-4" v-if="executions.length > 0">
                    <div 
                        v-for="execution in executions" 
                        :key="execution.execution_id"
                        class="rounded-lg border border-gray-300 bg-white p-6 shadow-sm dark:border-gray-600 dark:bg-gray-800"
                    >
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <div class="flex items-center space-x-3">
                                    <div class="shrink-0">
                                        <CheckCircleIcon
                                            v-if="execution.status === 'completed'"
                                            class="h-8 w-8 text-green-400"
                                        />
                                        <ExclamationCircleIcon
                                            v-else-if="execution.status === 'failed'"
                                            class="h-8 w-8 text-red-400"
                                        />
                                        <ClockIcon
                                            v-else
                                            class="h-8 w-8 text-yellow-400"
                                        />
                                    </div>
                                    <div>
                                        <h4 class="text-lg font-medium text-gray-900 dark:text-white">
                                            Execução {{ execution.execution_id.slice(0, 8) }}...
                                        </h4>
                                        <p class="text-sm text-gray-500 dark:text-gray-400">
                                            Iniciado em {{ formatDate(execution.started_at) }}
                                        </p>
                                    </div>
                                </div>

                                <div class="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                                        <dd class="mt-1">
                                            <span 
                                                :class="{
                                                    'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300': execution.status === 'completed',
                                                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300': execution.status === 'failed',
                                                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300': execution.status === 'running'
                                                }"
                                                class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                            >
                                                {{ getStatusLabel(execution.status) }}
                                            </span>
                                        </dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Devices</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                                            {{ execution.total_devices || 0 }}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Jobs</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                                            {{ execution.jobs_dispatched || 0 }}
                                        </dd>
                                    </div>
                                    <div v-if="execution.duration_seconds">
                                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Duração</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                                            {{ formatDuration(execution.duration_seconds) }}
                                        </dd>
                                    </div>
                                </div>

                                <div class="mt-4" v-if="execution.options">
                                    <h5 class="text-sm font-medium text-gray-500 dark:text-gray-400">Opções:</h5>
                                    <div class="mt-2 flex flex-wrap gap-2">
                                        <span 
                                            v-if="execution.force"
                                            class="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                                        >
                                            Force
                                        </span>
                                        <span 
                                            v-if="execution.dry_run"
                                            class="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                                        >
                                            Dry Run
                                        </span>
                                        <span 
                                            v-if="execution.ios_only"
                                            class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-900 dark:text-gray-300"
                                        >
                                            iOS Only
                                        </span>
                                        <span 
                                            v-if="execution.android_only"
                                            class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-900 dark:text-gray-300"
                                        >
                                            Android Only
                                        </span>
                                        <span class="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                                            Chunk: {{ execution.chunk_size }}
                                        </span>
                                        <span 
                                            v-if="execution.limit"
                                            class="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900 dark:text-orange-300"
                                        >
                                            Limit: {{ execution.limit }}
                                        </span>
                                    </div>
                                </div>

                                <div class="mt-4" v-if="execution.error_message">
                                    <h5 class="text-sm font-medium text-red-500 dark:text-red-400">Erro:</h5>
                                    <p class="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {{ execution.error_message }}
                                    </p>
                                </div>

                                <div class="mt-4" v-if="execution.min_id && execution.max_id">
                                    <h5 class="text-sm font-medium text-gray-500 dark:text-gray-400">Range de IDs:</h5>
                                    <p class="mt-1 text-sm text-gray-900 dark:text-white">
                                        {{ execution.min_id }} até {{ execution.max_id }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <empty-state
                    v-else-if="!loading"
                    title="Nenhuma execução encontrada"
                    description="Não há execuções de subscrição de tópicos para exibir."
                />
            </div>
        </div>
    </div>
</template>

<script>
import { 
    ClockIcon, 
    CheckCircleIcon, 
    ExclamationCircleIcon,
    DevicePhoneMobileIcon 
} from "@heroicons/vue/24/outline";
import TictoLoading from "../../components/layouts/TictoLoading.vue";
import Filters from "../../components/layouts/Filters.vue";
import VueSelect from "../../components/forms/VueSelect.vue";
import EmptyState from "../../components/layouts/EmptyState.vue";

export default {
    components: {
        TictoLoading,
        Filters,
        VueSelect,
        EmptyState,
        ClockIcon,
        CheckCircleIcon,
        ExclamationCircleIcon,
        DevicePhoneMobileIcon,
    },
    data() {
        return {
            loading: false,
            executions: [],
            filters: {
                status: null,
                date_from: null,
                date_to: null,
            },
            statusOptions: [
                { label: 'Concluído', value: 'completed' },
                { label: 'Falhou', value: 'failed' },
                { label: 'Em execução', value: 'running' },
            ],
        };
    },
    mounted() {
        this.fetch();
    },
    methods: {
        fetch() {
            this.loading = true;
            const params = { ...this.filters };
            
            axios.get('/api/mobile-devices/topic-subscription-runs', {
                params: {
                    ...params,
                },
            })
            .then(({ data }) => {
                this.executions = data;
                this.loading = false;
            })
            .catch((error) => {
                console.error('Erro ao buscar execuções:', error);
                this.loading = false;
            });
        },
        formatDate(dateString) {
            if (!dateString) return '-';
            
            return new Intl.DateTimeFormat('pt-BR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }).format(new Date(dateString));
        },
        formatDuration(seconds) {
            if (!seconds) return '-';
            
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            
            if (minutes > 0) {
                return `${minutes}m ${remainingSeconds}s`;
            }
            return `${seconds}s`;
        },
        getStatusLabel(status) {
            const statusLabels = {
                'completed': 'Concluído',
                'failed': 'Falhou',
                'running': 'Em execução',
            };
            return statusLabels[status] || status;
        },
    },
};
</script>